import {
  addDays,
  compareIso,
  dayOfMonth,
  daysBetween,
  formatIsoDate,
  type IsoDate,
  monthLabel,
  startOfWeek,
  WEEKDAY_INITIALS,
} from "@larder/shared";
import { useCallback, useMemo, useState, useSyncExternalStore } from "react";
import { ItemRow } from "../components/ItemRow";
import type { TrackedItem } from "../data/items";
import type { LarderState } from "../data/useLarder";

/**
 * The mirror calendar.
 *
 * GitHub's grid looks backwards only. Ours is centred on today, because the
 * half that changes behaviour is the FUTURE half: "Thursday is dark, five
 * things land" is a meal-prep trigger. The past half is the record — the
 * guilt-and-reward axis that brings people back.
 *
 * Laid out as real weeks, aligned to weekday columns. A flat run of cells is
 * unreadable: you cannot find "next Thursday" in it, which is the single most
 * common thing a person wants from this screen.
 *
 * A week is a COLUMN on a desktop and a ROW on a phone, which is the same grid
 * read against the axis each screen has to spare. The markup does not change:
 * one flat run of cells, week by week, that CSS flows down-then-across on a
 * wide screen and across-then-down on a narrow one. See the stylesheet.
 *
 * See docs/product/calendar.md.
 */

const WEEKS_BACK = 4;
const WEEKS_FORWARD = 4;

/* A desktop turns weeks sideways, and a sideways week costs ~30px of width
   instead of ~40px of height — so it can afford a far longer run. Four months
   of record and ten weeks of forecast, against nine weeks on a phone. */
const WIDE_BACK = 16;
const WIDE_FORWARD = 10;

/** Matches the layout breakpoint in styles.css. Kept in sync by hand. */
const WIDE = "(min-width: 64rem)";

/**
 * The range is a data decision, not a styling one — a phone must not pay to
 * fetch and render 27 weeks it will never show — so it is read in JS rather
 * than left to a media query.
 */
function useWide(): boolean {
  const subscribe = useCallback((onChange: () => void) => {
    const mq = window.matchMedia(WIDE);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(WIDE).matches,
    () => false,
  );
}

export function Calendar({ state }: { state: LarderState }) {
  const [selected, setSelected] = useState<IsoDate | null>(null);
  const wide = useWide();

  const { weeks, months, landing, soonCount } = useMemo(() => {
    // Everything still in the house, by the day it comes due.
    const byDay = new Map<IsoDate, TrackedItem[]>();
    for (const item of [...state.active, ...state.needsAnswer]) {
      const day = item.derived.effectiveExpiry;
      if (!day) continue;
      const list = byDay.get(day);
      if (list) list.push(item);
      else byDay.set(day, [item]);
    }

    const back = wide ? WIDE_BACK : WEEKS_BACK;
    const forward = wide ? WIDE_FORWARD : WEEKS_FORWARD;
    const first = startOfWeek(addDays(state.today, -back * 7));
    const rows = Array.from({ length: back + forward + 1 }, (_, w) =>
      Array.from({ length: 7 }, (_, d) => {
        const iso = addDays(first, w * 7 + d);
        return {
          iso,
          isToday: iso === state.today,
          isFuture: compareIso(iso, state.today) > 0,
          wasted: state.wasteDays.has(iso),
          clean: state.activityDays.has(iso) && !state.wasteDays.has(iso),
          due: byDay.get(iso) ?? [],
        };
      }),
    );

    // The line that actually changes what someone cooks this week.
    const soon = [...state.active, ...state.needsAnswer].filter((i) => {
      const d = i.derived.daysLeft;
      return d !== null && d >= 0 && d <= 7;
    }).length;

    /* One label per week COLUMN, on the week the month turns over — the way a
       contribution graph is labelled. The phone reads the same fact off the
       cell itself, because there is no room above a 40px column for a word. */
    const ticks = rows.map((week, w) => {
      const turns = week.find((d) => dayOfMonth(d.iso) === 1);
      return {
        key: addDays(first, w * 7),
        label: turns ? monthLabel(turns.iso) : "",
      };
    });

    return { weeks: rows, months: ticks, landing: byDay, soonCount: soon };
  }, [state, wide]);

  const selectedItems = selected ? (landing.get(selected) ?? []) : [];

  return (
    <section aria-label="calendar">
      {/* No heading, and no line when there is nothing to report.
          "Your record, and what's coming" described the grid immediately below
          it, which describes itself; "No streak yet — resolve something to
          start one" restated the top bar's "nothing due" in more words. Two
          lines of chrome above the one thing on the screen.

          What survives is the part that only exists when it is true: an actual
          streak, and an actual count of what lands this week. */}
      {(state.streak > 0 || soonCount > 0) && (
        <p className="tagline" data-testid="streak">
          {[
            state.streak > 0 ? `${state.streak}-day clean streak` : null,
            soonCount > 0 ? `${soonCount} due this week` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      )}

      <div className="cal">
        {/* Wide only. Hidden on a phone, where the month rides in the cell. */}
        <div className="cal-months" aria-hidden="true">
          {months.map((m) => (
            <span key={m.key}>{m.label}</span>
          ))}
        </div>

        <div className="cal-head">
          {WEEKDAY_INITIALS.map((initial, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed 7-day header
            <span key={i}>{initial}</span>
          ))}
        </div>

        <div className="grid" data-testid="calendar-grid">
          {weeks.flat().map((d) => {
            const intensity = Math.min(d.due.length, 4);
            const tone = d.wasted
              ? "wasted"
              : d.clean
                ? "clean"
                : d.due.length > 0
                  ? `load-${intensity}`
                  : "";
            const label = `${formatIsoDate(d.iso)}${
              d.due.length ? ` — ${d.due.length} due` : ""
            }`;
            return (
              <button
                key={d.iso}
                type="button"
                className={[
                  "cell",
                  tone,
                  d.isToday ? "today" : "",
                  selected === d.iso ? "picked" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                title={label}
                aria-label={label}
                onClick={() => setSelected(selected === d.iso ? null : d.iso)}
                data-testid={d.isToday ? "cell-today" : undefined}
              >
                {/* The 1st anchors the grid in real time without a month row.
                    Wide screens get that row, and hide this. */}
                <span className="cal-tick">
                  {dayOfMonth(d.iso) === 1 ? monthLabel(d.iso) : ""}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="legend">
        <span>
          <i className="cell clean" /> clean day
        </span>
        <span>
          <i className="cell wasted" /> waste
        </span>
        <span>
          <i className="cell load-3" /> expiring
        </span>
      </div>

      {selected && (
        <div className="bucket" data-testid="day-detail">
          <h3>
            {formatIsoDate(selected)}
            <span className="count">{daysBetween(state.today, selected)}d</span>
          </h3>
          {selectedItems.length === 0 ? (
            <p className="note">Nothing due.</p>
          ) : (
            selectedItems.map((item) => <ItemRow key={item.id} item={item} />)
          )}
        </div>
      )}
    </section>
  );
}
